import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  FinanceValueObjectsLedgerAccountBalanceTypeEnum,
  LedgerAccountFragment,
  PayRollConceptWithLedgerAccountResourcesFragment,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  LedgerAccountSelector,
  formatAccountName,
} from 'modules/banking/components/LedgerAccountSelector'
import { useUpsertLedgerAccountResource } from 'modules/finance/lib/accounting/useUpsertLedgerAccountResource'
import type { PayrollConceptLegalEntity } from 'modules/finance/pages/Accounting/PayrollConceptPage'

interface Props {
  payrollConcept: PayRollConceptWithLedgerAccountResourcesFragment
  legalEntity: PayrollConceptLegalEntity
  accounts: LedgerAccountFragment[]
  canManage: boolean
}

export const PayrollConceptRow = ({ payrollConcept, legalEntity, accounts, canManage }: Props) => {
  const accountResource = payrollConcept.accountResources[0]
  const accountingResourceId = accountResource?.id
  const accountId = accountResource?.account?.id
  const balanceType = accountResource?.balanceType

  const { upsertLedgerAccountResource, loading: savingLedgerAccount } =
    useUpsertLedgerAccountResource()

  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(accountId ?? null)
  const [selectedBalanceType, setSelectedBalanceType] =
    useState<FinanceValueObjectsLedgerAccountBalanceTypeEnum | null>(balanceType ?? null)

  useEffect(() => {
    setSelectedAccountId(accountId ?? null)
    setSelectedBalanceType(balanceType ?? null)
  }, [accountId, balanceType, legalEntity.id, payrollConcept.id])

  const linkAccount = useUnsafeCallback(
    async (
      newAccountId: number,
      newBalanceType: FinanceValueObjectsLedgerAccountBalanceTypeEnum | null
    ) => {
      const result = await upsertLedgerAccountResource({
        id: accountingResourceId,
        legalEntityId: legalEntity.id,
        accountId: newAccountId,
        resourceId: payrollConcept.id,
        resourceType: 'payrollconcept',
        balanceType: newBalanceType,
      }).then((res) => {
        track(trackingEvents.accounting.settings.payrollConcepts.editLedgerAccount)
        return res
      })

      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
          time: 2000,
        })
      }
    },
    [upsertLedgerAccountResource, accountingResourceId, legalEntity.id, payrollConcept.id]
  )

  const onSelectLedgerAccount = useUnsafeCallback(
    (id) => {
      setSelectedAccountId(id)
      if (selectedBalanceType) {
        linkAccount(id, selectedBalanceType)
      }
    },
    [setSelectedAccountId, linkAccount, selectedBalanceType]
  )

  const onSelectBalanceType = useUnsafeCallback(
    (type) => {
      setSelectedBalanceType(type)
      if (selectedAccountId) {
        linkAccount(selectedAccountId, type)
      }
    },
    [setSelectedBalanceType, selectedAccountId, linkAccount]
  )

  const account = useUnsafeMemo(
    () => accounts.find((a) => a.id == selectedAccountId),
    [accounts, selectedAccountId]
  )

  interface SelectOption {
    label: string
    value: FinanceValueObjectsLedgerAccountBalanceTypeEnum
  }

  const balanceTypeOptions: SelectOption[] = [
    {
      value: 'credit',
      label: i18n.t('finance.settings.accounting.credit'),
    },
    {
      value: 'debit',
      label: i18n.t('finance.settings.accounting.debit'),
    },
  ]

  return (
    <Box padding="s0" paddingTop={'s32'}>
      <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween" gap="s16">
        <Box width="s600">
          <Text size={'400'} weight="semibold">
            {payrollConcept.name}
          </Text>
        </Box>
        <Box width="full">
          <LedgerAccountSelector
            legalEntityId={legalEntity.id}
            currentLedgerAccountName={formatAccountName(account)}
            accounts={accounts}
            canManage={canManage}
            disabled={savingLedgerAccount}
            onSelect={onSelectLedgerAccount}
          />
        </Box>
        <Box width="s300">
          <Select
            readonly={savingLedgerAccount || !selectedAccountId}
            options={balanceTypeOptions}
            onChange={onSelectBalanceType}
            defaultValue={''}
            value={selectedBalanceType || ''}
            none={i18n.t('common.select')}
          />
        </Box>
      </Box>
    </Box>
  )
}
